<template>
  <MediaOverlay
    :is-open="isOpen"
    :type="props.data.mediaType"
    :video="props.data.video || props.data.externalVideo"
    @close="handleClose"
  />
  <button
    :class="[bem(''), props.class]"
    :style="
      props.data.theme &&
      `--theme-bg: ${props.data.theme.buttonBackgroundColour.hex};
      --theme-text: ${props.data.theme.buttonTextColour.hex};
      `
    "
    @mouseenter="handleHover(true)"
    @mouseleave="handleHover(false)"
    @click="handleClick"
  >
    <div :class="bem('inner')">
      <DatoCmsImage
        v-if="props.data.image"
        :data="props.data.image.responsiveImage"
        object-fit="cover"
        :object-position="`${props.data.image.focalPoint.x * 100}% ${
          props.data.image.focalPoint.y * 100
        }%`"
        layout="fill"
        :class="bem('image')"
      />
      <div :class="bem('text-button', { 'has-theme': props.data.theme })">
        <span :class="bem('text')"> {{ props.data.text }}</span>
        <VisionIconButton
          icon="play"
          size="x-small"
          background-colour="white"
          icon-colour="black"
          :fake-button="true"
          :parent-hover="isHovered"
          :class="bem('icon-button')"
        />
      </div>
    </div>
  </button>
</template>

<script setup lang="ts">
import { Image as DatoCmsImage } from 'vue-datocms'
const { $track } = useNuxtApp() as any

interface MediaOverlayButtonProps {
  data: {
    mediaType: string
    text: string
    image: any
    theme?: any
    video?: any
    externalVideo?: any
  }
  class: any
  trackingCategory?: string
  trackingId: string
}

const props = defineProps<MediaOverlayButtonProps>()
const bem = useBEM('MediaOverlayButton')

const isHovered = ref<boolean>(false)
const isOpen = ref<boolean>(false)

const handleHover = function (hover: boolean) {
  isHovered.value = hover
}

const handleClick = async function () {
  isOpen.value = true

  await $track.global.linkClicked({
    interactionElement: 'Media Overlay Button',
    interactionSection: props.trackingId,
    interactionCategory: props.trackingCategory || '',
    linkText: props.data.text
  }, 'Clicked Media Overlay Button')
}

const handleClose = function () {
  isOpen.value = false
}
</script>

<style lang="postcss">
.MediaOverlayButton {
  --height: var(--media-overlay-button-height, 120px);
  --width: var(--media-voerlay-button-width, auto);
  --aspect-ratio: var(--media-overlay-button-aspect-ratio, 16 / 9);
  --scrim-opacity: var(--media-overlay-button-scrim-opacity, 0.15);

  @apply relative p-1
  h-[var(--height)] w-[var(--width)] aspect-[var(--aspect-ratio)]
  backdrop-blur-light bg-opacity-10 rounded-lg overflow-clip;
}

.MediaOverlayButton__inner {
  @apply relative h-full w-full rounded-md overflow-clip;
}

.MediaOverlayButton__image {
  @apply transition-transform duration-300;
}

.MediaOverlayButton__text-button {
  @apply relative h-full w-full p-2 z-[2]
  bg-black bg-opacity-[var(--scrim-opacity)]
  flex justify-between items-end gap-4;
}

.MediaOverlayButton__text {
  @apply font-bold text-base leading-tighter
  text-white text-left;
}

/* Hover
*/
.MediaOverlayButton:hover {
  .MediaOverlayButton__image {
    @apply scale-105;
  }
}

/* Theme
*/
.MediaOverlayButton__text-button--has-theme .MediaOverlayButton__icon-button {
  --vision-icon-button-bg-colour: var(--theme-bg);
  --vision-icon-button-icon-colour: var(--theme-text);
}
</style>
